import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  LabelList,
  Tooltip,
  ComposedChart,
  Line,
  Legend,
  CartesianGrid,
} from "recharts";
import { categoryBars, slaBars, mttrBars, rootCauses } from "../data/mockData";
import {
  qualityEffectivenessRows,
  qualityEffectivenessStats,
  qualityEffectivenessInsight,
} from "../data/qualityEffectivenessData";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { hasModuleAccess } from "../lib/moduleAccess";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KPI = ({ label, value, valueColor, delta, deltaColor }) => (
  <div
    className="rounded-md p-4"
    style={{
      background: "#111420",
      border: "1px solid rgba(255,255,255,0.12)",
    }}
    data-testid={`analytics-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    <div
      style={{
        fontFamily: "DM Mono, monospace",
        fontSize: 9,
        letterSpacing: "1.4px",
        color: "#9198b0",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
    <div
      className="mt-3"
      style={{
        fontFamily: "DM Mono, monospace",
        fontSize: 28,
        color: valueColor,
        fontWeight: 500,
        lineHeight: 1,
      }}
    >
      {value}
    </div>
    <div className="mt-2" style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: deltaColor }}>
      {delta}
    </div>
  </div>
);

const Card = ({ title, children, className }) => (
  <div
    className={"rounded-md p-4 " + (className || "")}
    style={{
      background: "#111420",
      border: "1px solid rgba(255,255,255,0.12)",
    }}
  >
    <div
      className="mb-4"
      style={{
        fontFamily: "Syne, sans-serif",
        fontWeight: 700,
        fontSize: 13,
        color: "#f0f2f8",
        letterSpacing: "0.3px",
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const HorizontalBar = ({ label, value, suffix, percent, color }) => (
  <div className="mb-3 last:mb-0">
    <div className="flex justify-between items-center mb-1.5">
      <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 500, fontSize: 12, color: "#cfd1d8" }}>
        {label}
      </span>
      <span style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color, minWidth: 50, textAlign: "right" }}>
        {value}
        {suffix}
      </span>
    </div>
    <div className="relative rounded-full overflow-hidden" style={{ height: 6, background: "#0d0f18" }}>
      <div
        className="absolute top-0 bottom-0 left-0 rounded-full"
        style={{ width: `${percent}%`, background: color, transition: "width 800ms ease" }}
      />
    </div>
  </div>
);

export default function Analytics() {
  const { t } = useT();
  const { user } = useAuth();
  const sortingScoped = hasModuleAccess(user, "sorting");
  const [pareto, setPareto] = useState(null);

  useEffect(() => {
    if (!sortingScoped) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/analytics/sorting-pareto`);
        if (!cancelled) setPareto(data);
      } catch (_) {
        if (!cancelled) setPareto(null);
      }
    })();
    return () => { cancelled = true; };
  }, [sortingScoped]);

  return (
    <div data-testid="analytics-view">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1
            style={{
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 800,
              fontSize: "clamp(26px, 3.4vw, 34px)",
              color: "#f0f2f8",
              letterSpacing: "-0.5px",
              lineHeight: 1.05,
            }}
            data-testid="view-title"
          >
            {t("analytics.title")}
          </h1>
          <div className="mt-1.5" style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#9198b0" }}>
            {t("analytics.subtitle")}
          </div>
        </div>
        <button
          className="px-3 py-2 rounded-md self-start sm:self-auto"
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "#cfd1d8",
            fontFamily: "Syne, sans-serif",
            fontWeight: 600,
            fontSize: 12,
          }}
          data-testid="8d-pdf-btn"
        >
          {t("analytics.btn_8d_pdf")}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KPI label={t("analytics.kpi_incidents_month")} value="47" valueColor="#f0f2f8" delta={`↑ 12% ${t("analytics.vs_last")}`} deltaColor="#ff7e7c" />
        <KPI label={t("analytics.kpi_sla_breach")} value="16%" valueColor="#E24B4A" delta={t("analytics.target_label", { n: 10 })} deltaColor="#9198b0" />
        <KPI label={t("analytics.kpi_mttr_all")} value="6.8h" valueColor="#f0f2f8" delta={`↓ 1.2h ${t("analytics.vs_last")}`} deltaColor="#4ed084" />
        <KPI label={t("analytics.kpi_8d_closed")} value="31" valueColor="#4ed084" delta={`↑ 8 ${t("analytics.vs_last")}`} deltaColor="#4ed084" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title={t("analytics.card_category")}>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={categoryBars} margin={{ top: 20, right: 10, bottom: 0, left: -20 }}>
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#9198b0", fontFamily: "DM Mono, monospace", fontSize: 10 }}
                  axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#9198b0", fontFamily: "DM Mono, monospace", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "#0d0f18",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 4,
                    fontFamily: "DM Mono, monospace",
                    fontSize: 11,
                  }}
                  cursor={{ fill: "rgba(255,255,255,0.04)" }}
                />
                <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                  {categoryBars.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                  <LabelList
                    dataKey="value"
                    position="top"
                    style={{ fill: "#cfd1d8", fontFamily: "DM Mono, monospace", fontSize: 10 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title={t("analytics.card_sla_compliance")}>
          <div>
            {slaBars.map((s) => (
              <HorizontalBar
                key={s.name}
                label={s.name}
                value={s.value}
                suffix="%"
                percent={s.value}
                color={s.color}
              />
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-4">
        <Card title={t("analytics.card_mttr_supplier")}>
          <div>
            {mttrBars.map((m) => (
              <HorizontalBar
                key={m.name}
                label={m.name}
                value={m.hours}
                suffix="h"
                percent={m.percent}
                color={m.color}
              />
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-4">
        <Card title={t("analytics.card_root_causes")}>
          <div>
            {rootCauses.map((r) => (
              <HorizontalBar
                key={r.name}
                label={r.name}
                value={`×${r.count}`}
                suffix=""
                percent={r.percent}
                color={r.color}
              />
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-4" data-testid="quality-effectiveness-section">
        <Card title={t("analytics.card_quality_effectiveness")}>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div
              className="rounded-md p-3"
              style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)" }}
              data-testid="qe-stat-avg-initial"
            >
              <div
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 9,
                  letterSpacing: "1.4px",
                  color: "#9198b0",
                  textTransform: "uppercase",
                }}
              >
                {t("analytics.kpi_avg_initial_rpn")}
              </div>
              <div
                className="mt-2"
                style={{ fontFamily: "'DM Mono', monospace", fontSize: 24, color: "#E24B4A", fontWeight: 500, lineHeight: 1 }}
              >
                {qualityEffectivenessStats.avgInitial}
              </div>
            </div>
            <div
              className="rounded-md p-3"
              style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)" }}
              data-testid="qe-stat-avg-new"
            >
              <div
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 9,
                  letterSpacing: "1.4px",
                  color: "#9198b0",
                  textTransform: "uppercase",
                }}
              >
                {t("analytics.kpi_avg_new_rpn")}
              </div>
              <div
                className="mt-2"
                style={{ fontFamily: "'DM Mono', monospace", fontSize: 24, color: "#4ed084", fontWeight: 500, lineHeight: 1 }}
              >
                {qualityEffectivenessStats.avgNew}
              </div>
            </div>
            <div
              className="rounded-md p-3"
              style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)" }}
              data-testid="qe-stat-avg-reduction"
            >
              <div
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 9,
                  letterSpacing: "1.4px",
                  color: "#9198b0",
                  textTransform: "uppercase",
                }}
              >
                {t("analytics.kpi_avg_reduction")}
              </div>
              <div
                className="mt-2"
                style={{ fontFamily: "'DM Mono', monospace", fontSize: 24, color: "#9ec3ec", fontWeight: 500, lineHeight: 1 }}
              >
                {qualityEffectivenessStats.avgReduction}%
              </div>
            </div>
          </div>

          <div style={{ width: "100%", height: 280, minHeight: 280 }}>
            <ResponsiveContainer>
              <ComposedChart data={qualityEffectivenessRows} margin={{ top: 10, right: 16, bottom: 0, left: -16 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                <XAxis
                  dataKey="id"
                  tick={{ fill: "#9198b0", fontFamily: "'DM Mono', monospace", fontSize: 9 }}
                  axisLine={{ stroke: "rgba(255,255,255,0.10)" }}
                  tickLine={false}
                  interval={0}
                  angle={-30}
                  dy={10}
                  height={50}
                />
                <YAxis
                  yAxisId="rpn"
                  tick={{ fill: "#9198b0", fontFamily: "'DM Mono', monospace", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 480]}
                />
                <YAxis
                  yAxisId="pct"
                  orientation="right"
                  tick={{ fill: "#9ec3ec", fontFamily: "'DM Mono', monospace", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  contentStyle={{
                    background: "#0d0f18",
                    border: "1px solid rgba(255,255,255,0.18)",
                    borderRadius: 6,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 11,
                    color: "#f0f2f8",
                  }}
                  cursor={{ fill: "rgba(255,255,255,0.04)" }}
                />
                <Legend
                  wrapperStyle={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c5cad8", paddingTop: 8 }}
                />
                <Bar yAxisId="rpn" dataKey="initial" name={t("analytics.legend_initial_rpn")} fill="#E24B4A" radius={[2, 2, 0, 0]} />
                <Bar yAxisId="rpn" dataKey="current" name={t("analytics.legend_new_rpn")} fill="#4ed084" radius={[2, 2, 0, 0]} />
                <Line
                  yAxisId="pct"
                  type="monotone"
                  dataKey="rollingReduction"
                  name={t("analytics.legend_rolling")}
                  stroke="#9ec3ec"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "#9ec3ec" }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div
            className="mt-3 px-3 py-2 rounded-md"
            style={{
              background: "rgba(243,156,18,0.08)",
              border: "1px solid rgba(243,156,18,0.30)",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              color: "#f3b14a",
              lineHeight: 1.55,
              letterSpacing: "0.3px",
            }}
            data-testid="qe-insight"
          >
            ▸ {qualityEffectivenessInsight}
          </div>
        </Card>
      </div>

      {sortingScoped && pareto && pareto.items && pareto.items.length > 0 && (
        <div className="mt-4" data-testid="analytics-defect-pareto-section">
          <Card title={t("analytics.card_defect_pareto") === "analytics.card_defect_pareto"
            ? "Defect Pareto · All Sorting Results"
            : t("analytics.card_defect_pareto")}>
            <div
              className="mb-3"
              style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0", letterSpacing: "0.4px" }}
              data-testid="analytics-defect-pareto-summary"
            >
              {pareto.result_count} sorting result(s) across {pareto.incident_count} incident(s)
            </div>
            <div style={{ width: "100%", height: Math.max(180, pareto.items.length * 32), minHeight: 180 }}>
              <ResponsiveContainer>
                <BarChart data={pareto.items.slice(0, 10)} layout="vertical" margin={{ left: 4, right: 28, top: 4, bottom: 4 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "#9198b0", fontFamily: "DM Mono, monospace", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis
                    dataKey="type"
                    type="category"
                    tick={{ fill: "#cfd1d8", fontFamily: "DM Mono, monospace", fontSize: 11 }}
                    width={130}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(255,255,255,0.04)" }}
                    contentStyle={{
                      background: "#0d0f18",
                      border: "1px solid rgba(255,255,255,0.18)",
                      borderRadius: 6,
                      fontFamily: "DM Mono, monospace",
                      fontSize: 11,
                      color: "#f0f2f8",
                    }}
                  />
                  <Bar dataKey="count" radius={[0, 3, 3, 0]}>
                    {pareto.items.slice(0, 10).map((d, i) => (
                      <Cell key={d.type} fill={i === 0 ? "#f3b14a" : "#185FA5"} />
                    ))}
                    <LabelList dataKey="count" position="right" fill="#f0f2f8" style={{ fontFamily: "DM Mono, monospace", fontSize: 10 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
